import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';

import { Preloader } from '#src/atoms/loader/Preloader/index.tsx';
import { BrandL } from '#src/atoms/typography/index.tsx';
import { CardFront } from '#src/components/card/Front/index.tsx';
import { ClaimRewardsDialog } from '#src/components/rewards/ClaimRewardsDialog/index.tsx';
import { Discount } from '#src/components/rewards/Discount/index.tsx';
import type { Reward } from '#src/components/rewards/types.ts';
import { useReferralRewards } from '#src/hooks/referral/useReferralRewards.tsx';
import { fantasy, glossary } from '#src/lib/glossary.ts';

import type { ClaimReferralRewardsDialog_referralReward } from './__generated__/index.graphql.ts';

type Props = {
  open: boolean;
  onClose: () => void;
  referralRewards: Array<ClaimReferralRewardsDialog_referralReward>;
};

const formatReward = (
  referralReward: ClaimReferralRewardsDialog_referralReward,
  teasers: ReactElement[],
  cardBack: ReactElement | null,
  claimed: boolean
): Reward | undefined => {
  if (!cardBack) {
    return undefined;
  }
  const { card, conversionCredit } = referralReward;

  return {
    ids: [referralReward.id],
    key: referralReward.id,
    backgroundText: conversionCredit ? (
      <FormattedMessage {...glossary.discount} />
    ) : (
      card?.rarityTyped || ''
    ),
    header: (
      <BrandL>
        <FormattedMessage {...fantasy.referralReward} />
      </BrandL>
    ),
    back: cardBack,
    front: isClaimed => {
      return conversionCredit ? (
        <Discount
          readyToShow={!!isClaimed}
          percentage={conversionCredit.percentageDiscount}
          maxDiscount={conversionCredit.maxDiscount}
        />
      ) : (
        <CardFront src={card?.pictureUrl || ''} />
      );
    },
    teasers,
    claimed,
  };
};

export const ClaimReferralRewardsDialog = ({
  open,
  onClose,
  referralRewards,
}: Props) => {
  const { claim, isClaimed, makeCardBackAndTeaser } = useReferralRewards();

  if (referralRewards.length === 0) {
    return null;
  }

  const { formattedRewards, groupedRewards } = referralRewards.reduce<{
    formattedRewards: Reward[];
    groupedRewards: Record<string, ClaimReferralRewardsDialog_referralReward>;
  }>(
    (result, referralReward) => {
      const { cardback, teasers } = makeCardBackAndTeaser(referralReward);
      const formattedReward = formatReward(
        referralReward,
        teasers,
        cardback,
        isClaimed(referralReward)
      );
      if (formattedReward) {
        result.formattedRewards.push(formattedReward);
      }
      result.groupedRewards[referralReward.id] = referralReward;
      return result;
    },
    {
      formattedRewards: [],
      groupedRewards: {},
    }
  );

  return (
    <>
      <Preloader
        imageUrls={referralRewards
          .map(reward => reward.card?.pictureUrl)
          .filter(url => !!url)}
      />
      <ClaimRewardsDialog
        open={open}
        onClose={onClose}
        onClaim={ids => {
          ids.forEach(id => {
            const reward = groupedRewards[id];
            if (reward) {
              claim(reward);
            }
          });
        }}
        rewards={formattedRewards}
      />
    </>
  );
};

ClaimReferralRewardsDialog.fragments = {
  referralReward: gql`
    fragment ClaimReferralRewardsDialog_referralReward on ReferralReward {
      id
      card: anyCard {
        slug
        pictureUrl
        rarityTyped
      }
      conversionCredit {
        id
        maxDiscount {
          ...Discount_MonetaryAmount
        }
        percentageDiscount
      }
      ...useReferralRewards_referralReward
    }
    ${Discount.fragments.MonetaryAmount}
    ${useReferralRewards.fragments.referralReward}
  ` as TypedDocumentNode<ClaimReferralRewardsDialog_referralReward>,
};
